import { computed, toValue, type MaybeRefOrGetter } from 'vue';

enum MessageContentPartTypes {
  TEXT = 'text',
  LINK = 'link',
}

type MessageContentPart =
  | {
      type: MessageContentPartTypes.TEXT;
      value: string;
    }
  | {
      type: MessageContentPartTypes.LINK;
      href: string;
      value: string;
    };

const urlPattern: RegExp = /https?:\/\/[^\s<>"']+/giu;
const trailingUrlPunctuationPattern: RegExp = /[.,!?;:]+$/u;

const getSafeHttpUrl = (value: string): string | null => {
  try {
    const parsedUrl: URL = new URL(value);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
      ? parsedUrl.href
      : null;
  } catch {
    return null;
  }
};

const parseMessageContentLinks = (content: string): MessageContentPart[] => {
  const parts: MessageContentPart[] = [];
  let contentCursor: number = 0;

  for (const match of content.matchAll(urlPattern)) {
    const rawUrl: string = match[0];
    const linkText: string = rawUrl.replace(trailingUrlPunctuationPattern, '');
    const linkHref: string | null = getSafeHttpUrl(linkText);
    const matchIndex: number = match.index ?? 0;

    if (!linkHref) {
      continue;
    }

    if (matchIndex > contentCursor) {
      parts.push({
        type: MessageContentPartTypes.TEXT,
        value: content.slice(contentCursor, matchIndex),
      });
    }

    parts.push({ type: MessageContentPartTypes.LINK, href: linkHref, value: linkText });
    contentCursor = matchIndex + linkText.length;
  }

  if (contentCursor < content.length) {
    parts.push({ type: MessageContentPartTypes.TEXT, value: content.slice(contentCursor) });
  }

  return parts;
};

const useMessageContentLinks = (content: MaybeRefOrGetter<string>) => {
  const parts = computed<MessageContentPart[]>(() => parseMessageContentLinks(toValue(content)));

  return { parts };
};

export { MessageContentPartTypes, useMessageContentLinks };
